import { REPLY_METHOD_LABEL, REPLY_METHOD_OPTIONS } from "@/pages/contact/requestFormsContent";
import { pickLang, useLanguage } from "@/i18n/LanguageContext";
import { GENERIC_STRINGS } from "@/pages/contact/requestFormsContent";

/**
 * Shared "Preferred reply method" radio group.
 *
 * Legacy behaviour (all 5 pre-Phase-24 forms) marks this required with a
 * red asterisk. Phase 24's V2 feedback flow allows anonymous submissions,
 * so pass `optional` when the user hasn't yet given any contact details:
 * the label switches to a soft "(optional)" tag and no asterisk is
 * rendered.
 */
export default function PreferredReplyField({
  value,
  onChange,
  error,
  optional = false,
  testIdPrefix = "reply",
}) {
  const { lang } = useLanguage();
  const g = GENERIC_STRINGS[lang];
  return (
    <fieldset
      className="mt-2"
      aria-invalid={error ? "true" : undefined}
      data-testid={`${testIdPrefix}-field`}
    >
      <legend className="text-[13px] font-semibold text-[color:var(--sas-ink)] mb-3">
        {pickLang(REPLY_METHOD_LABEL, lang)}
        {optional ? (
          <span className="text-[color:var(--sas-ink-2)] ms-1 font-normal">
            {g.optional}
          </span>
        ) : (
          <span className="text-[color:#b91c1c] ms-1" aria-hidden>*</span>
        )}
      </legend>
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 flex-wrap">
        {REPLY_METHOD_OPTIONS.map((opt) => {
          const checked = value === opt.key;
          return (
            <label
              key={opt.key}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 cursor-pointer transition-colors text-[14px] ${
                checked
                  ? "border-[color:var(--sas-ink)] bg-[color:var(--sas-accent-soft)]/60"
                  : "border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)]"
              }`}
              data-testid={`${testIdPrefix}-option-${opt.key}`}
            >
              <input
                type="radio"
                name="preferred_reply"
                value={opt.key}
                checked={checked}
                onChange={() => onChange(opt.key)}
                className="sr-only"
              />
              <span
                className={`inline-block w-3.5 h-3.5 rounded-full border ${
                  checked
                    ? "border-[color:var(--sas-ink)] bg-[color:var(--sas-ink)]"
                    : "border-[color:var(--sas-ink-2)]"
                }`}
                aria-hidden
              />
              <span className="text-[color:var(--sas-ink)]">{pickLang(opt, lang)}</span>
            </label>
          );
        })}
      </div>
      {error && (
        <p className="mt-2 text-[12.5px] text-[#b91c1c]" data-testid={`${testIdPrefix}-error`}>
          {error}
        </p>
      )}
    </fieldset>
  );
}
